// modal behaviour — scrim + centered dialog lifecycle for cleanSlate.
//
// cleanSlate extends base directly (there is no bootstrap.Modal here), so the base
// modal component (core/themes/base/components/modal/modal.js) drives this adapter
// through the contract's modal.create(el) -> { show, hide, dispose }. The component
// owns the markup (the zit-modal <template>, rendered into <body> before create());
// this adapter owns the runtime: the full-screen scrim, body scroll-lock, ESC- and
// backdrop-click-to-dismiss, wiring every [data-zit-dismiss="modal"] control, a
// focus trap, and the enter/leave transitions.
//
// zitModalHidden — the contract's neutral dismiss event (see modal.create in
// behaviors.contract.md). base's SHARED confirm.js and session.js listen for it on
// the modal element to run their teardown (removeModal / dispose). bootstrap5 relays
// it from Bootstrap's native 'hidden.bs.modal'; cleanSlate has no such framework
// event, so this adapter dispatches it directly — once the leave transition
// completes, with a timer fallback so a missed transitionend can't strand the
// teardown.

// Fallback (ms) if the leave transition's transitionend never fires (reduced motion,
// a display:none ancestor, etc.) so the dialog can't stay half-closed.
const LEAVE_TRANSITION_FALLBACK = 300;

// Focusable descendants for the focus trap / initial focus.
const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
].join(',');

// How many modals are currently open — the body scroll-lock is released only when
// the last one closes (confirm/popup modals can briefly overlap).
let openModalCount = 0;

function createModalInstance(el) {
    let isOpen = false;
    let previouslyFocused = null;

    const dialog = el.querySelector('[data-zit-subelement-type="dialog"]');

    // Backdrop click: dismiss only when the press starts AND ends on the scrim
    // itself (el), never when a drag began inside the dialog and released on the
    // backdrop (e.g. selecting text).
    let pointerDownOnBackdrop = false;
    const onPointerDown = function (e) {
        pointerDownOnBackdrop = (e.target === el);
    };
    const onClick = function (e) {
        if (pointerDownOnBackdrop && e.target === el) {
            instance.hide();
        }
        pointerDownOnBackdrop = false;
    };

    // ESC closes; Tab is trapped within the dialog.
    const onKeydown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            instance.hide();
            return;
        }
        if (e.key === 'Tab') {
            trapTab(e);
        }
    };

    const trapTab = function (e) {
        const focusables = Array.from(el.querySelectorAll(FOCUSABLE))
            .filter((node) => node.offsetParent !== null || node === document.activeElement);
        if (focusables.length === 0) {
            e.preventDefault();
            dialog.focus();
            return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    // Every close control inside the modal (header X + footer close button).
    const dismissers = Array.from(el.querySelectorAll('[data-zit-dismiss="modal"]'));
    const onDismissClick = function (e) {
        e.preventDefault();
        instance.hide();
    };

    // Shared open→closed cleanup used by both hide() (animated + teardown event)
    // and dispose() (silent). Runs at most once per open (callers guard on isOpen):
    // drop the document/backdrop listeners, release the scroll-lock when the last
    // open modal closes, start the fade-out, and restore focus.
    const closeCleanup = function () {
        document.removeEventListener('keydown', onKeydown, true);
        el.removeEventListener('pointerdown', onPointerDown);
        el.removeEventListener('click', onClick);

        openModalCount = Math.max(0, openModalCount - 1);
        if (openModalCount === 0) {
            document.body.classList.remove('zit-cs-modal-open');
        }

        el.classList.remove('zit-cs-modal-shown');

        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
            previouslyFocused.focus();
        }
        previouslyFocused = null;
    };

    const instance = {
        show: function () {
            if (isOpen) {
                return;
            }
            isOpen = true;
            previouslyFocused = document.activeElement;

            openModalCount += 1;
            document.body.classList.add('zit-cs-modal-open');

            // Display first, force a reflow, then flip the shown class so the
            // opacity/transform enter transition actually runs.
            el.classList.add('zit-cs-modal-active');
            void el.offsetWidth;
            el.classList.add('zit-cs-modal-shown');

            document.addEventListener('keydown', onKeydown, true);
            el.addEventListener('pointerdown', onPointerDown);
            el.addEventListener('click', onClick);

            // Focus the first focusable control, falling back to the dialog itself.
            const firstFocusable = el.querySelector(FOCUSABLE);
            (firstFocusable || dialog).focus();
        },

        // Dismiss (user-initiated: X / footer close / ESC / backdrop, or a
        // programmatic close). Fires the neutral zitModalHidden once the leave
        // transition completes — base's shared confirm.js / session.js listen for
        // it to run their own teardown.
        hide: function () {
            if (!isOpen) {
                return;
            }
            isOpen = false;
            closeCleanup();

            // Finalize after the leave transition: fully hide the element and fire
            // the teardown event. Guarded so transitionend + the fallback timer
            // can't double-run.
            let finalized = false;
            const finalize = function () {
                if (finalized) {
                    return;
                }
                finalized = true;
                el.removeEventListener('transitionend', onTransitionEnd);
                el.classList.remove('zit-cs-modal-active');
                el.dispatchEvent(new Event('zitModalHidden', { bubbles: true }));
            };
            const onTransitionEnd = function (e) {
                // Only the scrim's own opacity transition counts (ignore bubbled
                // transitions from descendants).
                if (e.target === el) {
                    finalize();
                }
            };
            el.addEventListener('transitionend', onTransitionEnd);
            setTimeout(finalize, LEAVE_TRANSITION_FALLBACK);
        },

        // Teardown of the widget itself. SILENT — deliberately does NOT dispatch
        // zitModalHidden: dispose is what confirm/session call *from* their
        // zitModalHidden handler (after OK / on removal), so re-dispatching would
        // re-enter that handler and double-dispose (this.modal is already false by
        // then). Mirrors Bootstrap, which fires hidden.bs.modal on hide but not on
        // dispose — so the bootstrap5 relay is silent on dispose too.
        dispose: function () {
            if (isOpen) {
                isOpen = false;
                closeCleanup();
                el.classList.remove('zit-cs-modal-active');
            }
            dismissers.forEach((btn) => btn.removeEventListener('click', onDismissClick));
        }
    };

    dismissers.forEach((btn) => btn.addEventListener('click', onDismissClick));

    return instance;
}

const modal = {
    create: function (el) {
        return createModalInstance(el);
    }
};

export default modal;
